<template>
  <span>
    <slot name="button" v-bind="{ onclick: toggle }">
      <b-button variant="primary" class="mr-1" size="xs" @click="toggle"
        ><i class="fas fa-edit"></i>&nbsp;Edit</b-button
      >
    </slot>

    <b-modal
      @show="onShow"
      @shown="onShown"
      @hidden="resetModal"
      ref="modal-container"
      v-model="modalShow"
      size="lg"
      :title="title"
      :static="true"
      :lazy="true"
      hide-footer
    >
      <form ref="form" @submit.stop.prevent="handleSubmit">
        <b-form-group v-if="!this.create" label="_ID" label-for="id-input">
          <b-form-input
            id="id-input"
            v-model="formValues.id"
            autocomplete="off"
            disabled
          />
        </b-form-group>

        <b-form-group label="Name" label-for="name-input">
          <b-form-input
            id="name-input"
            v-model="formValues.name"
            v-validate="{ required: true }"
            :state="validateState('name-input')"
            aria-describedby="name-input-live-feedback"
            data-vv-name="name-input"
            data-vv-as="Name"
            autocomplete="off"
          />
          <b-form-invalid-feedback id="name-input-live-feedback">
            {{ veeErrors.first('name-input') }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Game type" label-for="gameType-input">
          <b-form-select
            id="gameType-input"
            v-model="formValues.gameType"
            v-validate="{ required: true }"
            :options="gameTypeOptions"
            :state="validateState('gameType-input')"
            aria-describedby="gameType-input-live-feedback"
            data-vv-name="gameType-input"
            data-vv-as="Game type"
            :disabled="!this.create"
          />
          <b-form-invalid-feedback id="gameType-input-live-feedback">
            {{ veeErrors.first('gameType-input') }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Table ID" label-for="tableId-input">
          <b-form-input
            id="tableId-input"
            v-model="formValues.tableId"
            v-validate="{ required: true }"
            :state="validateState('tableId-input')"
            aria-describedby="tableId-input-live-feedback"
            data-vv-name="tableId-input"
            data-vv-as="Table ID"
            :disabled="!this.create"
            type="number"
            autocomplete="off"
          />
          <b-form-invalid-feedback id="tableId-input-live-feedback">
            {{ veeErrors.first('tableId-input') }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Sources" label-for="sources-input">
          <multiselect
            id="sources-input"
            v-model="formValues.sourceIds"
            :options="sourceOptions"
            :custom-label="
              opt => this.$store.getters['sourcesModule/getName'](opt)
            "
            :multiple="true"
            :placeholder="$t('No sources')"
            :select-label="$t('Press enter to select')"
            :selected-label="$t('Selected')"
            :loading="!this.$store.getters['sourcesModule/loaded']"
            :disabled="true"
          />
          <b-form-invalid-feedback
            v-if="hasVirtualTableSourcesError"
            style="display: block;"
          >
            Virtual table cannot have sources. Please remove sources first.
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group>
          <b-form-checkbox
            id="isVirtualTable-input"
            v-model="formValues.isVirtualTable"
          >
            Is Virtual Table
          </b-form-checkbox>
        </b-form-group>

        <b-form-group
          v-if="formValues.isVirtualTable"
          label="Original Table ID"
          label-for="originalTable-input"
        >
          <b-form-input
            id="originalTable-input"
            v-model="formValues.originalTable"
            v-validate="{
              required: formValues.isVirtualTable,
              numeric: true,
              min_value: 1
            }"
            :state="validateState('originalTable-input')"
            aria-describedby="originalTable-input-live-feedback"
            data-vv-name="originalTable-input"
            data-vv-as="Original Table ID"
            type="number"
            autocomplete="off"
          />
          <b-form-invalid-feedback id="originalTable-input-live-feedback">
            {{ veeErrors.first('originalTable-input') }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group>
          <b-form-checkbox
            id="active-mod-input"
            v-model="formValues.activeMod"
            @change="handleActiveMod"
          >
            Active Mod for Roulette
          </b-form-checkbox>
        </b-form-group>

        <b-form-group label="Options" label-for="options-input">
          <div ref="options-editor"></div>
          <b-form-input
            id="options-editor-error"
            v-model="optionsEditorError"
            v-validate="{ is: '' }"
            :state="validateState('options-editor-error')"
            aria-describedby="options-editor-error-live-feedback"
            data-vv-name="options-editor-error"
            data-vv-as="Value"
            hidden
            autocomplete="off"
          />
          <b-form-invalid-feedback
            id="options-editor-error-live-feedback"
            v-if="veeErrors.first('options-editor-error')"
          >
            {{ optionsEditorError }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Logo desktop" label-for="logoDesktop-input">
          <div
            style="max-width: 200px"
            class="mb-2"
            v-if="formValues.options && formValues.options.pictureDesktop"
          >
            <b-img
              :src="
                makeLogoPath(formValues.options.pictureDesktop) +
                  '?t=' +
                  Date.now()
              "
              fluid
              alt="pictureDesktop"
            ></b-img>
          </div>
          <b-form-file
            v-model="formValues.pictureDesktop"
            no-drop
            placeholder="Choose a file here..."
          />
        </b-form-group>

        <b-form-group label="Logo mobile" label-for="logoDesktop-input">
          <div
            style="max-width: 200px"
            class="mb-2"
            v-if="formValues.options && formValues.options.pictureMobile"
          >
            <b-img
              :src="
                makeLogoPath(formValues.options.pictureMobile) +
                  '?t=' +
                  Date.now()
              "
              fluid
              alt="pictureMobile"
            ></b-img>
          </div>
          <b-form-file
            v-model="formValues.pictureMobile"
            no-drop
            placeholder="Choose a file here..."
          />
        </b-form-group>

        <b-form-group label="Default logo" label-for="logoDefault-input">
          <div
            style="max-width: 200px"
            class="mb-2"
            v-if="formValues.options && formValues.options.pictureDefault"
          >
            <b-img
              :src="
                cdnBackUrl +
                  '/' +
                  formValues.options.pictureDefault +
                  '?t=' +
                  Date.now()
              "
              fluid
              alt="pictureDefault"
            ></b-img>
          </div>
          <b-form-file
            v-model="formValues.pictureDefault"
            no-drop
            placeholder="Choose a file here..."
          />
        </b-form-group>
      </form>

      <b-button
        class="mt-2 float-right"
        variant="primary"
        @click="handleSubmit"
        :disabled="!isJsonValid"
        >Save</b-button
      >
    </b-modal>
  </span>
</template>
<style>
.jse-main {
  max-height: 600px !important;
}
</style>
<script>
import { getRouteIcon, ROUTE_TABLES } from '../../utils/routerIcons'
import Multiselect from 'vue-multiselect'
import { JSONEditor } from 'vanilla-jsoneditor'
import Vue from 'vue'
import cloneDeep from 'lodash/cloneDeep'

export default {
  data() {
    return {
      modalShow: false,
      formValues: this.getInitialValues(),
      optionsEditorError: '',
      optionsEditor: null,
      hasVirtualTableSourcesError: false,
      isJsonValid: true
    }
  },
  components: {
    Multiselect
  },
  props: {
    create: Boolean,
    title: String,
    defaultValues: Object
  },
  methods: {
    handleActiveMod() {
      if (this.formValues.activeMod) {
        // Получаем текущие данные из JSON-редактора
        let options = this.optionsEditor
          ? this.optionsEditor.get()
          : { json: this.formValues.options || {} }
        let optionsData = options.json || JSON.parse(options.text || '{}')

        // Добавляем параметры активного мода
        optionsData.isCroupierTable = true
        optionsData.timer = 20
        optionsData.cancelDelay = 3

        // Обновляем JSON-редактор
        if (this.optionsEditor) {
          this.optionsEditor.set({ json: optionsData })
        } else {
          this.formValues.options = optionsData
        }
      } else {
        // Получаем текущие данные из JSON-редактора
        let options = this.optionsEditor
          ? this.optionsEditor.get()
          : { json: this.formValues.options || {} }
        let optionsData = options.json || JSON.parse(options.text || '{}')

        // Удаляем параметры активного мода
        delete optionsData.isCroupierTable
        delete optionsData.timer
        delete optionsData.cancelDelay

        // Обновляем JSON-редактор
        if (this.optionsEditor) {
          this.optionsEditor.set({ json: optionsData })
        } else {
          this.formValues.options = optionsData
        }
      }
    },
    makeLogoPath(path) {
      if (!path) {
        return ''
      }

      if (path[0] === '/') {
        return this.cdnBackUrl + path
      }

      return this.cdnBackUrl + '/' + path
    },
    validateState(ref) {
      if (
        this.veeFields[ref] &&
        (this.veeFields[ref].dirty || this.veeFields[ref].validated)
      ) {
        return !this.veeErrors.has(ref)
      }
      return null
    },
    getInitialValues() {
      return {
        id: null,
        name: null,
        gameType: null,
        tableId: null,
        pictureDesktop: null,
        pictureMobile: null,
        pictureDefault: null,
        activeMod: false,
        options: {
          pictureDesktop: null,
          pictureMobile: null,
          pictureDefault: null,
          tableType: 0,
          isLeft: true
        },
        sourceIds: [],
        isVirtualTable: false,
        originalTable: null
      }
    },
    async onShow() {
      this.setDefaults()
      try {
        await this.$store.dispatch('gameTypes/loadList')
      } catch (error) {
        this.$toastr.e('Failed to load game types')
      }
    },
    onShown() {
      this.optionsEditor = new JSONEditor({
        target: this.$refs['options-editor'],
        props: {
          onChange: () => {
            this.checkJsonValidation()
          }
        }
      })
      this.optionsEditor.set({ json: this.formValues.options || {} })
      this.checkJsonValidation()
    },
    checkJsonValidation() {
      if (!this.optionsEditor) {
        this.isJsonValid = true
        return
      }

      const errors = this.optionsEditor.validate()
      if (errors.length > 0) {
        this.isJsonValid = false
        this.optionsEditorError = errors[0].message || 'The Options must be a valid json string'
      } else {
        this.isJsonValid = true
        this.optionsEditorError = ''
      }
    },
    resetModal() {
      this.setDefaults()
      if (this.optionsEditor) {
        this.optionsEditor.destroy()
        this.optionsEditor = null
      }
      this.isJsonValid = true
      this.optionsEditorError = ''
    },
    setDefaults() {
      let initialValues = this.getInitialValues()

      for (let field in initialValues) {
        Vue.set(this.formValues, field, initialValues[field])
      }

      this.hasVirtualTableSourcesError = false

      if (this.defaultValues) {
        for (let field in this.defaultValues) {
          Vue.set(this.formValues, field, cloneDeep(this.defaultValues[field]))
        }

        if (this.formValues.sourceIds) {
          if (!Array.isArray(this.formValues.sourceIds)) {
            this.formValues.sourceIds = []
          } else {
            this.formValues.sourceIds = this.formValues.sourceIds.map(id => String(id))
          }
        } else {
          this.formValues.sourceIds = []
        }

        // Проверяем наличие параметров активного мода в опциях
        if (
          this.formValues.options &&
          this.formValues.options.isCroupierTable === true &&
          this.formValues.options.timer !== undefined &&
          this.formValues.options.cancelDelay !== undefined
        ) {
          this.formValues.activeMod = true
        }

        if (
          this.formValues.isVirtualTable &&
          this.formValues.originalTable &&
          this.formValues.sourceIds &&
          Array.isArray(this.formValues.sourceIds) &&
          this.formValues.sourceIds.length > 0
        ) {
          this.hasVirtualTableSourcesError = true
        }
      }
    },
    hideModal() {
      this.$refs['modal-container'].hide()
    },
    toggle() {
      this.modalShow = !this.modalShow
    },
    handleSubmit() {
      this.checkJsonValidation()

      if (!this.isJsonValid) {
        return
      }

      if (this.formValues.isVirtualTable && !this.formValues.originalTable) {
        this.$toastr.e('If Is Virtual Table is checked, Original Table ID must be provided')
        return
      }

      if (!this.formValues.isVirtualTable && this.formValues.originalTable) {
        this.$toastr.e('If Original Table ID is set, Is Virtual Table must be checked')
        return
      }

      if (this.formValues.isVirtualTable && this.formValues.originalTable && this.formValues.sourceIds && this.formValues.sourceIds.length > 0) {
        this.$toastr.e('Virtual table cannot have sources. Please remove sources first.')
        return
      }

      this.$validator.validateAll().then(result => {
        if (!result) {
          return
        }

        const fd = new FormData()
        fd.append('name', this.formValues.name)
        fd.append('gameType', this.formValues.gameType)
        fd.append('tableId', this.formValues.tableId)
        if (this.formValues.isVirtualTable !== null && this.formValues.isVirtualTable !== undefined) {
          fd.append('isVirtualTable', this.formValues.isVirtualTable ? '1' : '')
        }
        if (this.formValues.originalTable !== null && this.formValues.originalTable !== undefined && this.formValues.originalTable !== '') {
          fd.append('originalTable', this.formValues.originalTable.toString())
        }
        if (this.formValues.sourceIds !== null && this.formValues.sourceIds !== undefined && Array.isArray(this.formValues.sourceIds)) {
          if (this.formValues.sourceIds.length > 0) {
            this.formValues.sourceIds.forEach(sourceId => {
              fd.append('sourceIds[]', sourceId)
            })
          } else {
            fd.append('sourceIds[]', '')
          }
        }
        if (this.formValues.pictureDesktop) {
          fd.append('pictureDesktop', this.formValues.pictureDesktop)
        }
        if (this.formValues.pictureMobile) {
          fd.append('pictureMobile', this.formValues.pictureMobile)
        }
        if (this.formValues.pictureDefault) {
          fd.append('pictureDefault', this.formValues.pictureDefault)
        }

        let content = this.optionsEditor.get()
        // Финальное JSON-представление опций
        let optionsString = content.text || JSON.stringify(content.json)

        fd.append('options', optionsString)

        this.$http({
          url: this.create
            ? '/api/v1/tables'
            : '/api/v1/tables/' + this.formValues.tableId,
          method: 'POST',
          data: fd,
          headers: { 'Content-Type': 'multipart/form-data' }
        })
          .then(() => {
            setTimeout(() => {
              this.$emit('finish')
            }, 500)

            this.hideModal()
            this.$toastr.i('Success')
          })
          .catch(err => {
            let message

            if (typeof err.response.data.message === 'string') {
              message = err.response.data.message
            } else {
              message = JSON.stringify(err.response.data.message)
            }

            this.$toastr.e(message)

            throw err
          })
      })
    }
  },
  watch: {
    'formValues.isVirtualTable'(newValue) {
      if (!newValue) {
        this.formValues.originalTable = null
      }
    }
  },
  computed: {
    sourceOptions() {
      return this.$store.getters['sourcesModule/list'].map(source => source.id)
    },
    backUrl() {
      return process.env.VUE_APP_BACK_URL
    },
    cdnBackUrl() {
      return process.env.VUE_APP_CDN_BACK_URL
    },
    routeIcon: () => getRouteIcon(ROUTE_TABLES),
    gameTypeOptions() {
      return this.$store.getters['gameTypes/list'].map(({ id, name }) => {
        return { value: id, text: `${name} (${id})` }
      })
    }
  },
  beforeMount() {},
  mounted() {
    this.setDefaults()
  },
  beforeDestroy() {
    if (this.optionsEditor) {
      this.optionsEditor.destroy()
      this.optionsEditor = null
    }
  }
}
</script>
